import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, ChevronDown, ShieldCheck, Link2 } from "lucide-react";

/**
 * Header dropdown for My Vault.
 * - Stationary safety-yellow "+" button (no FAB) shown next to the user name.
 * - Click reveals a portal-anchored menu with: "Add Certification", "Join Company".
 *
 * Props:
 *   onAddCertification(): open the add-cert modal
 *   onJoinCompany(): open the join-company modal
 *   showJoinCompany: whether to render the Join Company item (hide if already linked)
 */
export default function VaultHeaderMenu({ onAddCertification, onJoinCompany, showJoinCompany }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const positionMenu = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    // Position the 220px-wide menu beneath the button, right-aligned to it.
    const menuWidth = 240;
    const left = Math.min(r.right - menuWidth, window.innerWidth - menuWidth - 8);
    setCoords({ top: r.bottom + 8, left: Math.max(8, left) });
  };

  useEffect(() => {
    if (!open) return;
    positionMenu();
    const onScroll = () => positionMenu();
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-vault-menu]")) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (fn) => { setOpen(false); fn(); };

  return (
    <>
      <button
        ref={btnRef}
        data-testid="vault-header-add"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`h-12 w-12 md:h-14 md:w-14 inline-flex items-center justify-center border-2 transition-colors shrink-0 ${
          open
            ? "bg-safety-yellow text-black border-black shadow-brutal"
            : "bg-safety-yellow text-black border-black hover:bg-safety-yellowHover shadow-brutal"
        }`}
        aria-label="Vault actions"
      >
        <Plus className={`w-6 h-6 md:w-7 md:h-7 transition-transform duration-150 ${open ? "rotate-45" : ""}`} strokeWidth={3.5} />
      </button>

      {open && createPortal(
        <div
          data-vault-menu
          data-testid="vault-header-menu"
          role="menu"
          className="fixed z-[70] w-60 border-2 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-150"
          style={{ top: coords.top, left: coords.left, backgroundColor: "#0B1E3F", borderColor: "#F9D71C", color: "#fff" }}
        >
          <MenuItem
            testId="menu-add-cert"
            Icon={ShieldCheck}
            label="Add Certification"
            onClick={() => choose(onAddCertification)}
          />
          {showJoinCompany && (
            <MenuItem
              testId="menu-join-company"
              Icon={Link2}
              label="Join Company"
              onClick={() => choose(onJoinCompany)}
              divider
            />
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuItem({ Icon, label, onClick, testId, divider }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      className={`w-full h-12 px-4 inline-flex items-center gap-3 text-sm font-bold uppercase tracking-wider hover:bg-safety-yellow hover:text-black transition-colors text-left ${
        divider ? "border-t-2" : ""
      }`}
      style={divider ? { borderTopColor: "rgba(249,215,28,0.3)" } : undefined}
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={2.75} />
      {label}
    </button>
  );
}
